import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { HighlightOffRounded } from '@material-ui/icons';
import { JobStatusType } from '../../api-client/autogenerated';
import {
  PunchListExportOutput,
  PunchListExportProgress,
  PunchListExportSelection,
  getPunchListExportJobStatus,
  requestPunchListExport,
} from '../../models/api/punch-list-export';
import { downloadFileById } from '../../scripts/utils';

const POLL_INTERVAL_MS = 2000;

const useStyles = makeStyles((theme) =>
  createStyles({
    title: {
      fontWeight: 600,
      fontSize: 20,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
    },
    status: {
      marginBottom: theme.spacing(1),
    },
    progress: {
      height: 6,
      borderRadius: 3,
    },
    error: {
      color: theme.palette.error.main,
    },
  }),
);

type ExportState =
  | { phase: 'starting' }
  | { phase: 'running'; progress?: PunchListExportProgress }
  | { phase: 'ready'; output: PunchListExportOutput }
  | { phase: 'failed'; message: string };

interface PunchListExportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  selection: PunchListExportSelection;
}

/**
 * Runs one bulk punch list export and follows it to the file.
 *
 * The rendering happens in a background job, so this polls rather than waiting on a response.
 * Closing the dialog only stops the polling -- the job keeps running, notifies the requester when it
 * finishes, and its file stays in the export log.
 */
export default function PunchListExportDialog(props: PunchListExportDialogProps) {
  const { open, onClose, projectId, projectName, selection } = props;
  const classes = useStyles();

  const [state, setState] = useState<ExportState>({ phase: 'starting' });
  // One export per opening: without this the effect's second run in development would queue a
  // duplicate job.
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!open) {
      hasStarted.current = false;
      setState({ phase: 'starting' });
      return undefined;
    }
    if (hasStarted.current) return undefined;
    hasStarted.current = true;

    let isCurrent = true;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async (uniqueIdentifier: string) => {
      if (!isCurrent) return;
      try {
        const job = await getPunchListExportJobStatus(uniqueIdentifier);
        if (!isCurrent) return;

        if (job.status === JobStatusType.Complete) {
          setState({ phase: 'ready', output: job.output as PunchListExportOutput });
          return;
        }
        if (job.status === JobStatusType.Canceled) {
          setState({
            phase: 'failed',
            message: job.error || 'The export did not finish. Please try again.',
          });
          return;
        }

        setState({ phase: 'running', progress: job.progress as PunchListExportProgress });
        timer = setTimeout(() => poll(uniqueIdentifier), POLL_INTERVAL_MS);
      } catch {
        if (isCurrent) {
          setState({ phase: 'failed', message: 'Lost track of the export. Please try again.' });
        }
      }
    };

    requestPunchListExport(projectId, selection)
      .then(({ uniqueIdentifier }) => {
        if (isCurrent) poll(uniqueIdentifier);
      })
      .catch(() => {
        if (isCurrent) {
          setState({ phase: 'failed', message: 'Could not start the export. Please try again.' });
        }
      });

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
    // `selection` is a fresh object each render; the export is keyed to one opening of the dialog,
    // which `open` already captures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const handleDownload = useCallback(() => {
    if (state.phase !== 'ready') return;
    downloadFileById(state.output.fileId, `${projectName} Punch List.pdf`);
  }, [state, projectName]);

  const itemsTotal = state.phase === 'running' ? state.progress?.itemsTotal : undefined;
  const itemsCompleted = state.phase === 'running' ? state.progress?.itemsCompleted : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle disableTypography>
        <Typography className={classes.title}>Export Punch List</Typography>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {state.phase === 'failed' ? (
          <Typography className={classes.error}>{state.message}</Typography>
        ) : state.phase === 'ready' ? (
          <Typography>
            {state.output.itemCount === 1
              ? 'Your export is ready with 1 item.'
              : `Your export is ready with ${state.output.itemCount} items.`}
          </Typography>
        ) : (
          <>
            <Typography className={classes.status}>
              {itemsTotal
                ? `Preparing your export — ${itemsCompleted} of ${itemsTotal} items.`
                : 'Preparing your export…'}
            </Typography>
            <LinearProgress
              className={classes.progress}
              variant={itemsTotal ? 'determinate' : 'indeterminate'}
              value={itemsTotal ? ((itemsCompleted ?? 0) / itemsTotal) * 100 : undefined}
            />
            {/* Not body2: this theme sets it in capitals, which is a lot for a whole sentence. */}
            <Typography color="textSecondary" style={{ marginTop: 12, fontSize: 14 }}>
              Large exports can take a few minutes. You can close this window: we will send you a
              notification when your export is ready, and it will be waiting in the export log.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {state.phase === 'ready' && (
          <Button variant="contained" color="primary" onClick={handleDownload}>
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
